import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import * as Sentry from '@sentry/react';
import { store } from './app/store';
import { Provider as ChakraProvider } from './components/ui/provider';
import { SocketProvider } from './contexts/SocketContext';
import { ENVIRONMENT_HOSTS, LOCAL_HOSTS } from './config/api';
import App from './App';
import './index.css';
import './variables.css';

// i18n - Must be imported before App to initialize translations
import './i18n';

// Determine environment based on URL
const getEnvironment = (): string => {
  const hostname = window.location.hostname;
  if (hostname === ENVIRONMENT_HOSTS.production) return 'production';
  if (hostname === ENVIRONMENT_HOSTS.development) return 'development';
  return 'local';
};

const currentEnvironment = getEnvironment();
const isLocal = currentEnvironment === 'local';
const isProd = currentEnvironment === 'production';

// Initialize Sentry for error tracking
Sentry.init({
  dsn: 'https://1ecfc4e1a005846840479d8ef20ed61b@o4510703372402688.ingest.us.sentry.io/4510703375351808',
  environment: currentEnvironment,
  enabled: !isLocal,
  // Only send PII data in production (more secure for development)
  sendDefaultPii: isProd,
  _experiments: {
    enableLogs: true,
  },
  // Fallback filter: drop events from local environments
  beforeSend(event) {
    const host = window.location.hostname;
    if (
      LOCAL_HOSTS.includes(host as typeof LOCAL_HOSTS[number]) ||
      host.endsWith('.local')
    ) {
      return null;
    }
    return event;
  },
});

const container = document.getElementById('root');

if (!container) {
  throw new Error('Root element not found');
}

const root = createRoot(container);

root.render(
  <React.StrictMode>
    <ReduxProvider store={store}>
      <SocketProvider>
        <ChakraProvider>
          <App />
        </ChakraProvider>
      </SocketProvider>
    </ReduxProvider>
  </React.StrictMode>
);

